'use client';

import { useState } from 'react';
import { X, Check, CreditCard, Truck, Phone, Mail, MapPin, User, Lock } from 'lucide-react';
import { useCart } from '@/lib/cart-store';

export default function CheckoutModal() {
  const {
    items,
    isCheckoutOpen,
    closeCheckout,
    clearCart,
    getTotal,
  } = useCart();

  const [step, setStep] = useState<'form' | 'success'>('form');
  const [delivery, setDelivery] = useState<'pickup' | 'belgium' | 'europe' | 'world'>('pickup');
  const [payment, setPayment] = useState<'card' | 'transfer' | 'whatsapp'>('card');

  const total = getTotal();

  const deliveryCost = {
    pickup: 0,
    belgium: total >= 500 ? 0 : 15,
    europe: 35,
    world: 65,
  }[delivery];

  const grandTotal = total + deliveryCost;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('success');
    setTimeout(() => {
      clearCart();
      closeCheckout();
      setStep('form');
    }, 5000);
  };

  if (!isCheckoutOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <div className="relative bg-[#0a0a0f] border border-[#D4AF37]/30 max-w-3xl w-full my-auto max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0a0a0f] border-b border-[#D4AF37]/20 p-5 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">
              Finaliser la commande
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#F4E8C1]">
              {step === 'form' ? 'Paiement sécurisé' : 'Commande confirmée'}
            </h2>
          </div>
          <button
            onClick={() => {
              closeCheckout();
              setStep('form');
            }}
            className="w-9 h-9 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        {step === 'success' ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold-gradient flex items-center justify-center glow-gold-strong">
              <Check size={40} className="text-[#0a0a0f]" />
            </div>
            <h3 className="font-display text-2xl font-bold text-gold-gradient mb-3">
              Merci pour votre confiance
            </h3>
            <p className="text-[#F4E8C1]/75 leading-relaxed max-w-md mx-auto mb-6">
              Votre commande a bien été enregistrée. Notre atelier vous contactera
              dans les 24 heures pour confirmer les détails et organiser la
              livraison ou le retrait à la boutique d'Anvers.
            </p>
            <div className="inline-block px-5 py-3 bg-[#12121a] border border-[#D4AF37]/30 mb-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/85 mb-1">
                Numéro de commande
              </div>
              <div className="font-display text-lg font-bold text-[#F4E8C1]">
                MAT-{Date.now().toString().slice(-8)}
              </div>
            </div>
            <p className="text-xs text-[#F4E8C1]/45 italic font-serif">
              « Chaque pièce est façonnée à la main. Soyez patient : le temps
              est notre plus fidèle allié. »
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 sm:p-7 space-y-6">
            {/* Order summary */}
            <div className="bg-[#12121a] border border-[#D4AF37]/15 p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
                Récapitulatif
              </div>
              <div className="space-y-2 mb-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-3 text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[#D4AF37] font-semibold flex-shrink-0">
                        ×{item.quantity}
                      </span>
                      <span className="text-[#F4E8C1] truncate">
                        {item.name}
                      </span>
                    </div>
                    <span className="text-[#F4E8C1]/70 flex-shrink-0">
                      {(item.price * item.quantity).toLocaleString('fr-BE')} €
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#D4AF37]/15 pt-3 space-y-1">
                <div className="flex justify-between text-xs text-[#F4E8C1]/60">
                  <span>Sous-total articles</span>
                  <span>{total.toLocaleString('fr-BE')} €</span>
                </div>
                <div className="flex justify-between text-xs text-[#F4E8C1]/60">
                  <span>Livraison</span>
                  <span>
                    {deliveryCost === 0
                      ? 'Offerte'
                      : `${deliveryCost.toLocaleString('fr-BE')} €`}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#D4AF37]/15">
                  <span className="font-display text-base font-semibold text-[#F4E8C1]">
                    Total
                  </span>
                  <span className="font-display text-xl font-bold text-gold-gradient">
                    {grandTotal.toLocaleString('fr-BE')} €
                  </span>
                </div>
              </div>
            </div>

            {/* Delivery mode */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
                Mode de livraison
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'pickup', label: 'Retrait boutique', sub: 'Anvers · Gratuit', icon: MapPin },
                  { id: 'belgium', label: 'Belgique', sub: '24-48h · Offerte +500€', icon: Truck },
                  { id: 'europe', label: 'Europe', sub: '3-5j · 35 €', icon: Truck },
                  { id: 'world', label: 'Monde', sub: '5-10j · 65 €', icon: Truck },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDelivery(opt.id as any)}
                    className={`flex items-start gap-3 p-3 border text-left transition-all ${
                      delivery === opt.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                        : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/45'
                    }`}
                  >
                    <opt.icon size={16} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-[#F4E8C1]">
                        {opt.label}
                      </div>
                      <div className="text-[10px] text-[#F4E8C1]/55 mt-0.5">
                        {opt.sub}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Customer info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <FormField
                label="Prénom"
                icon={User}
                name="firstname"
                placeholder="Jean"
                required
              />
              <FormField
                label="Nom"
                icon={User}
                name="lastname"
                placeholder="Dupont"
                required
              />
              <FormField
                label="Email"
                icon={Mail}
                name="email"
                type="email"
                placeholder="jean@exemple.com"
                required
              />
              <FormField
                label="Téléphone"
                icon={Phone}
                name="phone"
                type="tel"
                placeholder="+32 ..."
                required
              />
            </div>

            {/* Address — if not pickup */}
            {delivery !== 'pickup' && (
              <div className="space-y-4">
                <FormField
                  label="Adresse"
                  icon={MapPin}
                  name="address"
                  placeholder="Rue, numéro"
                  required
                />
                <div className="grid sm:grid-cols-3 gap-4">
                  <FormField
                    label="Code postal"
                    name="zip"
                    placeholder="2000"
                    required
                  />
                  <FormField
                    label="Ville"
                    name="city"
                    placeholder="Anvers"
                    required
                  />
                  <FormField
                    label="Pays"
                    name="country"
                    placeholder="Belgique"
                    required
                  />
                </div>
              </div>
            )}

            {/* Payment */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
                Mode de paiement
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'card', label: 'Carte bancaire', icon: CreditCard },
                  { id: 'transfer', label: 'Virement', icon: Mail },
                  { id: 'whatsapp', label: 'WhatsApp', icon: Phone },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPayment(opt.id as any)}
                    className={`flex flex-col items-center gap-2 p-3 border transition-all ${
                      payment === opt.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                        : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/45'
                    }`}
                  >
                    <opt.icon size={20} className="text-[#D4AF37]" />
                    <span className="text-[10px] text-[#F4E8C1] font-medium text-center">
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Card details — if card */}
            {payment === 'card' && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <FormField
                    label="Numéro de carte"
                    icon={CreditCard}
                    name="card"
                    placeholder="0000 0000 0000 0000"
                    required
                  />
                </div>
                <FormField
                  label="Expiration"
                  name="exp"
                  placeholder="MM/AA"
                  required
                />
                <FormField
                  label="CVV"
                  icon={Lock}
                  name="cvv"
                  placeholder="123"
                  required
                />
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-3 py-4 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.2em] text-xs hover:glow-gold-strong transition-all duration-400"
            >
              <Lock size={14} />
              Confirmer — {grandTotal.toLocaleString('fr-BE')} €
            </button>

            <p className="text-center text-[9px] uppercase tracking-[0.2em] text-[#F4E8C1]/40 flex items-center justify-center gap-2">
              <Lock size={11} />
              Transaction chiffrée SSL · Vos données sont confidentielles
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function FormField({
  label,
  name,
  type = 'text',
  placeholder,
  required,
  icon: Icon,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <Icon
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]/60"
          />
        )}
        <input
          name={name}
          type={type}
          placeholder={placeholder}
          required={required}
          className={`w-full bg-[#0a0a0f] border border-[#D4AF37]/25 ${
            Icon ? 'pl-9' : 'pl-4'
          } pr-4 py-3 text-sm text-[#F4E8C1] placeholder:text-[#F4E8C1]/30 focus:border-[#D4AF37] focus:outline-none transition-colors`}
        />
      </div>
    </div>
  );
}
